import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useModalStore } from '../../stores/useUIStore';
import { useChatStore } from '../../stores/useChatStore';
import { useSocket } from '../../context/SocketContext';
import apiClient from '../../api/api';
import toast from 'react-hot-toast';

const MultimediaModal = ({ onSend }) => {
    const closeModal = useModalStore((state) => state.closeModal);
    const modals = useModalStore((state) => state.modals);
    const selectedConversation = useChatStore((state) => state.selectedConversation);
    const { socket } = useSocket();
    const isOpen = modals?.['multimediaModal']?.isOpen;
    const [activeTab, setActiveTab] = useState('upload');
    const [selectedFile, setSelectedFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    
    // Referencias para la Cámara
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const [isCameraActive, setIsCameraActive] = useState(false);

    // Estado del Álbum (Simulado por ahora)
    const [albumImages, setAlbumImages] = useState([]);

    const emitMediaState = useCallback((state) => {
        if (socket && selectedConversation?._id) {
            socket.emit('media-state', { conversationId: selectedConversation._id, state });
        }
    }, [socket, selectedConversation]);

    useEffect(() => {
        if (!isOpen) {
            stopCamera();
            setPreviewUrl(null);
            setSelectedFile(null);
            setIsUploading(false);
            emitMediaState(null);
        }
    }, [isOpen, emitMediaState]);

    useEffect(() => {
        if (!isOpen) return;
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                emitMediaState(null);
                closeModal('multimediaModal');
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, closeModal, emitMediaState]);

    const startCamera = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ video: true });
            if (videoRef.current) {
                videoRef.current.srcObject = stream;
                setIsCameraActive(true);
            }
        } catch (err) {
            toast.error("No se pudo acceder a la cámara. Verifica los permisos.");
        }
    };

    const stopCamera = () => {
        if (videoRef.current && videoRef.current.srcObject) {
            const tracks = videoRef.current.srcObject.getTracks();
            tracks.forEach(track => track.stop());
            videoRef.current.srcObject = null;
            setIsCameraActive(false);
        }
    };

    const capturePhoto = () => {
        if (videoRef.current && canvasRef.current) {
            const context = canvasRef.current.getContext('2d');
            context.drawImage(videoRef.current, 0, 0, 640, 480);
            canvasRef.current.toBlob(blob => {
                const file = new File([blob], "camera-capture.jpg", { type: "image/jpeg" });
                handleFileSelect(file);
                stopCamera();
            }, 'image/jpeg');
        }
    };

    const handleFileSelect = (file) => {
        if (!file) return;
        // Validación de peso (10MB)
        if (file.size > 10 * 1024 * 1024) {
            toast.error("El archivo es demasiado grande (Max 10MB)");
            return;
        }
        
        setSelectedFile(file);
        const url = URL.createObjectURL(file);
        setPreviewUrl(url);
    };

    const handleSendMedia = async () => {
        if (!selectedFile) return;
        
        setIsUploading(true);
        const uploadState = selectedFile.type?.startsWith('image/') ? 'uploading_image' : 'uploading_file';
        emitMediaState(uploadState);
        try {
            let uploadFile = selectedFile;

            if (selectedFile.type?.startsWith('image/')) {
                try {
                    // 🟢 Intentar comprimir si la librería está disponible
                    const imageCompressionModule = await import('browser-image-compression').catch(() => null);
                    if (imageCompressionModule?.default) {
                        const compressed = await imageCompressionModule.default(selectedFile, {
                            maxSizeMB: 1,
                            maxWidthOrHeight: 1920,
                            useWebWorker: true,
                            initialQuality: 0.8,
                        });
                        uploadFile = compressed instanceof File
                            ? compressed
                            : new File([compressed], selectedFile.name, { type: compressed.type || selectedFile.type });
                    }
                } catch (compressionError) {
                    console.warn('⚠️ Error al comprimir imagen, usando original:', compressionError);
                }
            }

            const formData = new FormData();
            formData.append('file', uploadFile); // 🟢 FIX: Cambiar a 'file' para coincidir con backend
            
            // 🟢 FIX: Usar el endpoint correcto /chat/upload
            const response = await apiClient.post('/chat/upload', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            
            const { url, type } = response.data;
            
            // Disparamos el callback para que el chat envíe el mensaje con la URL
            if (onSend) {
                onSend({ mediaUrl: url, type: type || 'image' });
            }
            
            closeModal('multimediaModal');
            toast.success("Multimedia lista para enviar");
        } catch (error) {
            console.error("Upload error", error);
            toast.error("Error al subir el archivo. Inténtalo de nuevo.");
        } finally {
            setIsUploading(false);
            emitMediaState(null);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 backdrop-blur-sm transition-opacity">
            <div className="bg-white dark:bg-gray-900 w-full max-w-lg rounded-xl overflow-hidden shadow-2xl transform transition-all">
                {/* Header Tabs */}
                <div className="flex border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
                    <button 
                        onClick={() => setActiveTab('album')}
                        className={`flex-1 py-3 font-semibold text-sm transition-colors ${activeTab === 'album' ? 'text-blue-600 border-b-2 border-blue-600 bg-white dark:bg-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
                    >
                        <i className="fas fa-images mr-2"></i>Mi Álbum
                    </button>
                    <button 
                        onClick={() => setActiveTab('upload')}
                        className={`flex-1 py-3 font-semibold text-sm transition-colors ${activeTab === 'upload' ? 'text-blue-600 border-b-2 border-blue-600 bg-white dark:bg-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
                    >
                        <i className="fas fa-cloud-upload-alt mr-2"></i>Subir
                    </button>
                    <button 
                        onClick={() => { setActiveTab('camera'); startCamera(); }}
                        className={`flex-1 py-3 font-semibold text-sm transition-colors ${activeTab === 'camera' ? 'text-blue-600 border-b-2 border-blue-600 bg-white dark:bg-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
                    >
                        <i className="fas fa-camera mr-2"></i>Cámara
                    </button>
                </div>

                {/* Content Area */}
                <div className="p-4 h-80 overflow-y-auto relative bg-gray-100 dark:bg-gray-900">
                    {activeTab === 'upload' && (
                        <div className="flex flex-col items-center justify-center h-full border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors bg-white dark:bg-gray-800">
                            {previewUrl ? (
                                <div className="relative w-full h-full p-2">
                                    <img src={previewUrl} alt="Preview" className="w-full h-full object-contain rounded" />
                                    <button onClick={() => { setPreviewUrl(null); setSelectedFile(null); }} className="absolute top-4 right-4 bg-red-500 text-white rounded-full p-1 shadow hover:bg-red-600">
                                        <i className="fas fa-times"></i>
                                    </button>
                                </div>
                            ) : (
                                <label className="cursor-pointer flex flex-col items-center w-full h-full justify-center">
                                    <i className="fas fa-cloud-upload-alt text-5xl text-blue-500 mb-3"></i>
                                    <span className="text-gray-600 dark:text-gray-300 font-medium">Click para seleccionar archivo</span>
                                    <span className="text-gray-400 text-xs mt-1">Imágenes, Videos o Audio (Max 10MB)</span>
                                    <input type="file" className="hidden" onChange={(e) => handleFileSelect(e.target.files[0])} accept="image/*,video/*,audio/*" />
                                </label>
                            )}
                        </div>
                    )}

                    {activeTab === 'camera' && (
                        <div className="flex flex-col items-center h-full bg-black rounded-lg overflow-hidden relative shadow-inner">
                            {!previewUrl ? (
                                <>
                                    <video ref={videoRef} autoPlay playsInline className="w-full h-full object-cover"></video>
                                    <div className="absolute bottom-4 flex gap-4">
                                        <button 
                                            onClick={capturePhoto}
                                            className="w-16 h-16 bg-white rounded-full border-4 border-gray-300 hover:scale-105 transition-transform shadow-lg"
                                        ></button>
                                    </div>
                                    <canvas ref={canvasRef} width="640" height="480" className="hidden"></canvas>
                                </>
                            ) : (
                                <div className="relative w-full h-full">
                                    <img src={previewUrl} alt="Capture" className="w-full h-full object-contain" />
                                    <button onClick={() => { setPreviewUrl(null); startCamera(); }} className="absolute top-4 right-4 bg-white text-black rounded-full px-3 py-1 shadow font-bold text-xs">
                                        Retomar
                                    </button>
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === 'album' && (
                        <div className="grid grid-cols-3 gap-2">
                            <div className="col-span-3 text-center text-gray-500 py-20 flex flex-col items-center">
                                <i className="fas fa-images text-4xl mb-3 text-gray-300"></i>
                                <p>Tu galería de Ágora está vacía por ahora.</p>
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer Actions */}
                <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-between items-center bg-white dark:bg-gray-900">
                    <button 
                        onClick={() => closeModal('multimediaModal')}
                        className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                    >
                        Cerrar
                    </button>
                    <button 
                        onClick={handleSendMedia}
                        disabled={!selectedFile || isUploading}
                        className={`px-6 py-2 rounded-lg flex items-center gap-2 font-semibold shadow-md transition-all ${
                            !selectedFile || isUploading 
                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                            : 'bg-blue-600 text-white hover:bg-blue-700 hover:shadow-lg'
                        }`}
                    >
                        {isUploading ? (
                            <>
                                <i className="fas fa-circle-notch fa-spin"></i> Subiendo...
                            </>
                        ) : (
                            <>
                                <i className="fas fa-paper-plane"></i> Enviar Ahora
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default MultimediaModal;
